import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  HotelChainsSearchRequest,
  HotelChainsDeleteRequest,
  HotelPropertyCodesSearchRequest,
  HotelPropertyCodesDeleteRequest,
  convertCriteriaUIToHotelChainsFamilySearchCriteria,
  convertCriteriaUIToHotelPropertyCodesFamilySearchCriteria,
  FamilyUi,
  FamilySearchCriteriaUI,
  HOTEL_PROP_CODES_TYPE,
  HOTEL_CHAINS_TYPE
} from '../model';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { NavigationService } from '../../service/core/navigation.service';
import {
  SearchFamilyHotelChainsAction,
  SearchFamilyHotelPropertyCodesAction,
  ActivateFamilyDisplayAction,
  DeleteFamilyDisplayNotificationAction,
  ClearFamiliesAction,
  DeleteFamilySearchNotificationAction,
  DeleteFamilyHotelChainsAction,
  DeleteFamilyHotelPropertyCodesAction,
  StartFamilyModificationAction,
  ClearFamiliesSelectionAction,
  ClearLookupHotelPropertyCodesFamiliesAction,
  ClearLookupHotelChainsFamiliesAction,
  DeleteFamilyLookupNotificationAction
} from '../../store/families/families-action';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { UppNotification } from '../../model/notification';
import {
  selectFamilySearchSending,
  selectFamilies,
  selectFamilySearchNotification,
  selectDeleteSending,
  selectSelectedFamilyNames,
  selectLookupHotelChainsFamilies,
  selectLookupHotelPropertyCodesFamilies,
  selectFamilyLookupNotification,
  selectFamilyCurrentPage,
  selectFamilyPageSize,
  selectFamilyTotalCount
} from '../../store/families/families-selector';
import {
  NotificationMessages,
  UppNotificationComponent
} from '../../components/upp-notification/upp-notification.component';
import { FamiliesSearchCriteriaComponent } from './families-search-criteria/families-search-criteria.component';
import { UserDetailsService } from '../../service/user-details.service';
import { UppComponent, UppComponentNames } from '../../service/model';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType,
  SearchResult
} from '../../base/search/basic-search-result-table/basic-search-result-table.component';
import { selectNavigation } from '../../store/navigation/navigation-selector';
import { HotelServiceElements } from '../../hotels/model/hotel-service-elements';
import { PERMISSION_MANAGE_FAMILY } from '../../service/user-permissions.service';
import { RouteURL } from '../../model/route-url';
import { AsyncPipe } from '@angular/common';
import { LoadingAlertComponent } from '../../components/loading-alert/loading-alert.component';
import { FamiliesState } from '../../store/families/families-state';
import { AgGridSearchTableComponent } from '../../base/search/ag-grid-search-table/ag-grid-search-table.component';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { TableActionsComponent } from '../../components/table-actions/table-actions.component';
import { FeatureFlags } from '../../core/util/resources';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent } from 'ag-grid-community';

@Component({
  selector: 'ama-ng-upp-families-search',
  templateUrl: './families-search.component.html',
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    FamiliesSearchCriteriaComponent,
    BasicSearchResultTableComponent,
    AsyncPipe,
    AgGridAngular
  ]
})
export class FamiliesSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  @ViewChild(FamiliesSearchCriteriaComponent)
  searchCriteriaComponent!: FamiliesSearchCriteriaComponent;

  familySearchSending$: Observable<boolean>;
  familyDeleteSending$: Observable<boolean>;
  selectedFamilyNames$: Observable<Set<string>>;
  families$: Observable<FamilyUi[]> | undefined;
  notificationSearch$: Observable<UppNotification | undefined>;
  notificationLookup$: Observable<UppNotification | undefined>;
  mainMessages: NotificationMessages;
  familyHotelType!: string;

  currentPage$!: Observable<number>;
  pageSize$!: Observable<number>;
  totalCount$!: Observable<number>;
  transformedFamilies$!: Observable<FamilyUi[]>;

  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  private lastSearchCriteria: FamilySearchCriteriaUI | null = null;

  selectedFamilyNames!: Set<string>;
  uppComponent = UppComponent.FAMILIES_SEARCH;

  managePermission = PERMISSION_MANAGE_FAMILY;

  agGridTablesActivated = false;
  paginationEnabled = false;
  partialNameMatchEnabled = false;

  displayableFields: DisplayableField[] = [
    {
      value: 'description',
      label: 'Description'
    }
  ];

  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);
  private readonly subscription = new Subscription();

  constructor(
    store: Store<FamiliesState>,
    router: Router,
    navigationService: NavigationService,
    readonly configurationService: ConfigurationService,
    private readonly userDetailsService: UserDetailsService
  ) {
    super(store, router, navigationService);

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((isEnabled) => {
        this.agGridTablesActivated = isEnabled ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.partialNameMatch).subscribe((isEnabled) => {
        this.partialNameMatchEnabled = isEnabled ?? false;
      })
    );

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.pagination).subscribe((isEnabled) => {
        this.paginationEnabled = isEnabled ?? false;
      })
    );

    this.familySearchSending$ = this.store.pipe(
      select(selectFamilySearchSending),
      map((sending) => !!sending)
    );
    this.familyDeleteSending$ = this.store.pipe(
      select(selectDeleteSending),
      map((sending) => !!sending)
    );
    this.notificationSearch$ = this.store.pipe(select(selectFamilySearchNotification));
    this.notificationLookup$ = this.store.pipe(select(selectFamilyLookupNotification));
    this.mainMessages = {
      error: $localize`:@@upp.global.text.mainErrorText:The request failed due to the following errors:`,
      warning: $localize`:@@upp.global.text.mainWarningText:Warnings were generated during the search process:`,
      success: $localize`:@@upp.global.text.mainSuccessText:The search was successful.`
    };
    this.selectedFamilyNames$ = this.store.pipe(select(selectSelectedFamilyNames));

    this.currentPage$ = this.store.pipe(select(selectFamilyCurrentPage));
    this.pageSize$ = this.store.pipe(select(selectFamilyPageSize));
    this.totalCount$ = this.store.pipe(select(selectFamilyTotalCount));

    this.transformedFamilies$ = combineLatest([
      this.store.pipe(select(selectFamilies)),
      this.currentPage$,
      this.pageSize$,
      this.totalCount$,
      this.familySearchSending$
    ]).pipe(
      map(([families, currentPage, pageSize, totalCount, isLoading]) => {
        const filtered = (families || []).filter((f) => f && !f.statusType);
        if (this.paginationEnabled && totalCount > 0) {
          const fullArray = Array.from({ length: totalCount }, (_, i) => ({ __idx: i })) as (
            | FamilyUi
            | { __idx: number }
          )[];
          if (!isLoading && filtered.length > 0) {
            const startIndex = currentPage * pageSize;
            filtered.forEach((family, index) => {
              fullArray[startIndex + index] = family;
            });
          }
          return fullArray as FamilyUi[];
        }
        return filtered;
      })
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.navigationService.setSelectedMenuTitle($localize`:@@upp.families.search.navigationTitle:Hotel Family Search`);
    this.navigationService.activateHotelFamilies();
    this.navigationService.enableNavigation();

    const selectedNamesSubscription = this.selectedFamilyNames$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedFamilyNamesSearchResults) => (this.selectedFamilyNames = selectedFamilyNamesSearchResults));
    this.subscription.add(selectedNamesSubscription);

    this.store.pipe(select(selectNavigation), take(1)).subscribe((lookupParam) => {
      if (lookupParam) {
        this.lookupOptions = lookupParam;
        this.lookupValues = lookupParam.data;
        this.lookup = !!lookupParam.lookup;
        if (lookupParam.fieldName) {
          this.familyHotelType =
            lookupParam.fieldName === HotelServiceElements.HOTEL_PROPERTY_CODES_FAMILIES
              ? HOTEL_PROP_CODES_TYPE
              : HOTEL_CHAINS_TYPE;
        }
      }
    });

    if (this.agGridTablesActivated) {
      this.initializeColumnDefs();
    }

    this.subscription.add(
      this.currentPage$.subscribe((page) => {
        this.currentPage = page;
      })
    );

    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        this.pageSize = size;
      })
    );

    this.subscription.add(
      this.totalCount$.subscribe((total) => {
        this.totalCount = total;
      })
    );

    if (this.lookup) {
      this.navigationService.disableNavigation();
      if (this.agGridTablesActivated) {
        this.updateColumnDefsForLookup();
      }
    }

    const navigationSubscription = this.store
      .pipe(select(selectNavigation), takeUntil(this.destroy$))
      .subscribe((lookupParam) => {
        if (lookupParam?.lookup) {
          this.navigationService.disableNavigation();
        }
      });
    this.subscription.add(navigationSubscription);

    if (this.lookup) {
      this.store.dispatch(new ClearFamiliesSelectionAction({}));
      this.store.dispatch(new DeleteFamilyLookupNotificationAction({}));
      this.getLookupFamilies();
    } else {
      this.getFamilies();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    super.ngOnDestroy();
  }

  searchFamilies(searchCriteria: FamilySearchCriteriaUI) {
    this.currentPage = 0;
    if (this.partialNameMatchEnabled) {
      searchCriteria.enableFullTextSearch = true;
    }
    if (searchCriteria.hotelFamilyType && searchCriteria.hotelFamilyType === HOTEL_PROP_CODES_TYPE) {
      this.createSearchRequestForCodes(searchCriteria);
    } else {
      this.createSearchRequestForChains(searchCriteria);
    }
    this.familyHotelType = searchCriteria.hotelFamilyType;

    this.lookup ? this.getLookupFamilies() : this.getFamilies();

    if (this.gridApi) {
      this.gridApi.paginationGoToPage(0);
    }
  }

  createSearchRequestForChains(searchCriteria: FamilySearchCriteriaUI) {
    this.lastSearchCriteria = searchCriteria;
    const request: HotelChainsSearchRequest = {
      version: '1.0',
      hotelChainsFamily: convertCriteriaUIToHotelChainsFamilySearchCriteria(searchCriteria)
    };
    request.hotelChainsFamily = this.userDetailsService.assignEntity(request.hotelChainsFamily);
    if (this.paginationEnabled && !this.lookup) {
      request.searchFollowUpRequest = {
        requestedPageNumber: String(this.currentPage + 1),
        numberOfRecords: String(this.pageSize)
      };
    }
    this.store.dispatch(new SearchFamilyHotelChainsAction({ request, lookup: this.lookup }));
  }

  createSearchRequestForCodes(searchCriteria: FamilySearchCriteriaUI) {
    this.lastSearchCriteria = searchCriteria;
    const request: HotelPropertyCodesSearchRequest = {
      version: '1.0',
      hotelPropertyCodesFamily: convertCriteriaUIToHotelPropertyCodesFamilySearchCriteria(searchCriteria)
    };
    request.hotelPropertyCodesFamily = this.userDetailsService.assignEntity(request.hotelPropertyCodesFamily);
    if (this.paginationEnabled && !this.lookup) {
      request.searchFollowUpRequest = {
        requestedPageNumber: String(this.currentPage + 1),
        numberOfRecords: String(this.pageSize)
      };
    }
    this.store.dispatch(new SearchFamilyHotelPropertyCodesAction({ request, lookup: this.lookup }));
  }

  closeNotification() {
    if (this.lookup) {
      this.store.dispatch(new DeleteFamilyLookupNotificationAction({}));
    } else {
      this.store.dispatch(new DeleteFamilySearchNotificationAction({}));
    }
  }

  clearFamilies() {
    if (this.lookup) {
      this.store.dispatch(
        this.isHotelPropertiesCodesFamily()
          ? new ClearLookupHotelPropertyCodesFamiliesAction({})
          : new ClearLookupHotelChainsFamiliesAction({})
      );
      this.store.dispatch(new ClearFamiliesSelectionAction({}));
      if (this.agGridTablesActivated) {
        this.lookupValues = [];
        this.families$ = of([]);
      }
    } else {
      this.store.dispatch(new ClearFamiliesAction({}));
      if (this.agGridTablesActivated) {
        this.families$ = of([]);
      }
    }
  }

  showFamily(familyId: string) {
    this.store.dispatch(new ActivateFamilyDisplayAction({ id: familyId }));
    this.store.dispatch(new DeleteFamilyDisplayNotificationAction({}));
    this.router.navigate([`${RouteURL.familyHotelDisplay}/${familyId}`]);
  }

  modifyFamily(familyId: string) {
    this.store.dispatch(new StartFamilyModificationAction({ id: familyId }));
    this.router.navigate([RouteURL.familyHotelModify]);
  }

  deleteFamilyChains(request?: HotelChainsDeleteRequest) {
    this.store.dispatch(new DeleteFamilyHotelChainsAction({ request }));
  }

  deleteFamilyCode(request?: HotelPropertyCodesDeleteRequest) {
    this.store.dispatch(new DeleteFamilyHotelPropertyCodesAction({ request }));
  }

  storeUnsavedFamilySearch() {
    if (!this.lookup) {
      this.searchCriteriaComponent.storeUnsavedFamilySearch();
    }
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.FAMILIES;
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => this.showFamily((data as FamilyUi).id ?? '');
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => this.modifyFamily((data as FamilyUi).id ?? '');
  }

  getCopyHandler(): (data: unknown) => void {
    return () => {}; // Copy is forbidden for families
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      if (this.isHotelPropertiesCodesFamily()) {
        const request: HotelPropertyCodesDeleteRequest = {
          version: '1.0',
          hotelPropertyCodesFamily: {
            id: (data as FamilyUi).id || '',
            name: (data as FamilyUi).name || '',
            version: (data as FamilyUi).version || 1
          }
        };
        this.deleteFamilyCode(request);
      } else {
        const request: HotelChainsDeleteRequest = {
          version: '1.0',
          hotelChainsFamily: {
            id: (data as FamilyUi).id || '',
            name: (data as FamilyUi).name || '',
            version: (data as FamilyUi).version || 1
          }
        };
        this.deleteFamilyChains(request);
      }
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return () => {};
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return this.familyDeleteSending$;
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null) {
    this.activeConfirmationRowId$.next(rowId);
  }

  getHotelChainsFamilyEntryType(): EntryType {
    return EntryType.HOTEL_CHAINS_FAMILY;
  }

  getHotelPropertyCodesFamilyEntryType(): EntryType {
    return EntryType.HOTEL_PROPERTY_CODES_FAMILY;
  }

  recordToSearchResult(family: FamilyUi): SearchResult {
    return {
      id: family.id,
      name: family.name,
      description: family.description,
      organization: family.organization,
      version: family.version
    };
  }

  isHotelPropertiesCodesFamily(): boolean {
    return this.familyHotelType === HOTEL_PROP_CODES_TYPE;
  }

  propertyCodesFamilyEntryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): HotelPropertyCodesDeleteRequest {
    return {
      version,
      hotelPropertyCodesFamily: {
        id,
        name,
        version: ruleVersion
      }
    };
  }

  propertyCodesFamilyEntryToUpdateNameResolver(entryToUpdate: HotelPropertyCodesDeleteRequest) {
    return entryToUpdate.hotelPropertyCodesFamily.name;
  }

  chainsFamilyEntryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): HotelChainsDeleteRequest {
    return {
      version,
      hotelChainsFamily: {
        id,
        name,
        version: ruleVersion
      }
    };
  }

  chainsFamilyEntryToUpdateNameResolver(entryToUpdate: HotelChainsDeleteRequest) {
    return entryToUpdate.hotelChainsFamily.name;
  }

  getNotification(): Observable<UppNotification | undefined> {
    if (this.lookup) {
      return this.notificationLookup$;
    }
    return this.notificationSearch$;
  }

  getSearchResults(): Observable<FamilyUi[] | undefined> {
    if (this.lookup) {
      return this.store.pipe(
        select(
          this.isHotelPropertiesCodesFamily() ? selectLookupHotelPropertyCodesFamilies : selectLookupHotelChainsFamilies
        ),
        map((families) => (families || []).filter((f) => !f?.statusType))
      );
    }
    return this.transformedFamilies$;
  }

  protected updateColumnDefsForLookup(): void {
    this.columnDefs = [
      this.columnDefs[0], // Name column
      this.columnDefs[1] // Description column
    ];
  }

  private getFamilies(): void {
    this.families$ = this.store.pipe(
      select(selectFamilies),
      map((families) => (families || []).filter((f) => !f?.statusType))
    );
  }

  private getLookupFamilies(): void {
    this.families$ = this.store.pipe(
      select(
        this.isHotelPropertiesCodesFamily() ? selectLookupHotelPropertyCodesFamilies : selectLookupHotelChainsFamilies
      ),
      map((families) => (families || []).filter((f) => !f?.statusType))
    );
  }

  override onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);

    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        if (!this.gridApi) {
          return;
        }
        if (size !== this.gridApi.paginationGetPageSize()) {
          this.gridApi.setGridOption('paginationPageSize', size);
        }
      })
    );
  }

  private initializeColumnDefs() {
    const nameColDef: ColDef = {
      headerName: 'Name',
      field: 'name',
      resizable: false,
      sortable: true,
      unSortIcon: true,
      suppressMovable: true,
      autoHeight: true
    };

    this.columnDefs = [
      nameColDef,
      {
        headerName: 'Description',
        field: 'description',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        minWidth: 250,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          familyDeleteSending$: this.familyDeleteSending$,
          copyForbidden: true
        }
      }
    ];

    this.gridOptions = {
      ...this.gridOptions,
      getRowId: (params) => params.data.id ?? `__ph_${params.data.__idx}`,
      getRowClass: (params) => (params.data?.id ? '' : 'ag-row-hidden'),
      postSortRows: (params) => {
        this.handlePostSortRowsForPagination(params, this.paginationEnabled);
      },
      onPaginationChanged: () => {
        if (!this.paginationEnabled || !this.gridApi || !this.lastSearchCriteria) {
          return;
        }
        // Only trigger a BE search when the API has returned pagination metadata.
        // If totalCount === 0 the API did not respond with searchFollowUpResponse,
        // so all records are already loaded and we let ag-grid paginate them FE-side.
        if (this.totalCount === 0) {
          return;
        }
        const newPage = this.gridApi.paginationGetCurrentPage();
        const newPageSize = this.gridApi.paginationGetPageSize();

        if (newPage !== this.currentPage || newPageSize !== this.pageSize) {
          this.currentPage = newPage;
          this.pageSize = newPageSize;

          if (this.familyHotelType === HOTEL_PROP_CODES_TYPE) {
            this.createSearchRequestForCodes(this.lastSearchCriteria);
          } else {
            this.createSearchRequestForChains(this.lastSearchCriteria);
          }
        }
      }
    };
  }
}
